export interface CaseStudyBlock {
  title: string;
  requirements?: string[];
  implementation?: string[];
  benefits?: string[];
}

export interface DomainExpertise {
  heading: string;
  items: string[];
}

export interface IndustrySectorItem {
  id: string;
  num: string;
  title: string;
  subtitle: string;
  badge: string;
  iconType: string;
  icon?: string;
  summary: string;
  shortDescription: string;
  keyPoints: string[];
  platform: string;
  fieldbus: string;
  safetyStd: string;
  deliverables: string[];
  machinery: string[];
  kpiStats: { label: string; value: string }[];
  tag: string;
  /* ── content from simatechautomation.in/service-details.html ── */
  siteIntro?: string;
  domainExpertise?: DomainExpertise[];
  caseStudies?: CaseStudyBlock[];
  sector?: string;
  scope?: string;
  name?: string;
  image?: string;
}

export interface IndustryTypographySettings {
  headingFontFamily?: string;
  headingFontSize?: string;
  headingFontWeight?: string;
  headingColor?: string;
  bodyFontFamily?: string;
  bodyFontSize?: string;
  bodyFontWeight?: string;
  bodyColor?: string;
  bodyLineHeight?: string;
  sectionLabelFontFamily?: string;
  sectionLabelFontSize?: string;
  sectionLabelColor?: string;
  sectionLabelTracking?: string;
  bulletStyle?: 'checkmark' | 'cyan-dot' | 'teal-circle' | 'chevron' | 'hyphen' | 'square';
  bulletColor?: string;
  cardBorderRadius?: 'rounded-none' | 'rounded-lg' | 'rounded-xl' | 'rounded-2xl';
  cardBgStyle?: 'white' | 'subtle-slate' | 'frosted';
  contentArrangementStyle?: 'executive-cards' | 'modern-split' | 'clean-minimalist';

  // ── Verified Field Case Studies Specific Typography & Styling ──
  caseStudySectionTitleFontFamily?: string;
  caseStudySectionTitleFontSize?: string;
  caseStudySectionTitleFontWeight?: string;
  caseStudySectionTitleColor?: string;
  caseStudySectionTitleTracking?: string;

  caseStudyHeaderBg?: string;
  caseStudyTitleFontFamily?: string;
  caseStudyTitleFontSize?: string;
  caseStudyTitleFontWeight?: string;
  caseStudyTitleColor?: string;
  caseStudyBadgeColor?: string;

  caseStudyColHeaderFontFamily?: string;
  caseStudyColHeaderFontSize?: string;
  caseStudyColHeaderFontWeight?: string;
  caseStudyColHeaderColor?: string;
  caseStudyColHeaderTracking?: string;

  caseStudyBodyFontFamily?: string;
  caseStudyBodyFontSize?: string;
  caseStudyBodyFontWeight?: string;
  caseStudyBodyColor?: string;
  caseStudyBodyLineHeight?: string;

  caseStudyBulletStyle?: 'checkmark' | 'cyan-dot' | 'teal-circle' | 'chevron' | 'hyphen' | 'square';
  caseStudyBulletColor?: string;
  caseStudyBenefitsBulletStyle?: 'checkmark' | 'cyan-dot' | 'teal-circle' | 'chevron' | 'hyphen' | 'square';
  caseStudyBenefitsBulletColor?: string;

  // ── Industry Cards Grid Common Typography & Card Styling ──
  gridHeadingText?: string;
  gridHeadingFontFamily?: string;
  gridHeadingFontSize?: string;
  gridHeadingFontWeight?: string;
  gridHeadingColor?: string;

  gridSubtitleText?: string;
  gridSubtitleFontFamily?: string;
  gridSubtitleFontSize?: string;
  gridSubtitleColor?: string;

  cardTitleFontFamily?: string;
  cardTitleFontSize?: string;
  cardTitleFontWeight?: string;
  cardTitleColor?: string;
  cardTitleActiveColor?: string;

  cardDescFontFamily?: string;
  cardDescFontSize?: string;
  cardDescFontWeight?: string;
  cardDescColor?: string;
  cardDescActiveColor?: string;
  cardDescLineHeight?: string;

  cardCapHeadingText?: string;
  cardCapHeadingFontFamily?: string;
  cardCapHeadingFontSize?: string;
  cardCapHeadingFontWeight?: string;
  cardCapHeadingColor?: string;
  cardCapHeadingActiveColor?: string;
  cardCapHeadingTracking?: string;

  cardBulletFontFamily?: string;
  cardBulletFontSize?: string;
  cardBulletFontWeight?: string;
  cardBulletLineHeight?: string;
  cardBulletColor?: string;
  cardBulletActiveColor?: string;
  cardBulletStyle?: 'cyan-dot' | 'teal-circle' | 'checkmark' | 'chevron' | 'hyphen' | 'square';
  cardBulletIconColor?: string;
  cardBulletActiveIconColor?: string;

  cardNormalBg?: string;
  cardNormalBorder?: string;
  cardActiveBg?: string;
  cardActiveBorder?: string;
  cardBorderRadiusStyle?: 'rounded-none' | 'rounded-lg' | 'rounded-xl' | 'rounded-2xl' | 'rounded-3xl';

  cardIconSize?: string;
  cardIconColor?: string;
  cardIconActiveColor?: string;

  cardBtnText?: string;
  cardBtnFontFamily?: string;
  cardBtnFontSize?: string;
  cardBtnFontWeight?: string;
  cardBtnColor?: string;
  cardBtnActiveColor?: string;
}

export const INDUSTRY_SECTORS: IndustrySectorItem[] = [
  {
    id: 'cable',
    num: '01',
    title: 'Cable Industry',
    subtitle: 'Power, JFTC & Fiber Optics',
    badge: 'Flagship Competency',
    iconType: 'cable',
    tag: 'cable',
    summary: 'Turnkey process automation for high-speed wire drawing, multi-axis capstan synchronization, continuous vulcanization (CCV), and fiber optic loose-tube buffering lines with sub-millisecond dancer tension regulation.',
    shortDescription: 'Turnkey process automation for high-speed wire drawing, multi-axis capstan synchronization, continuous vulcanization (CCV), and fiber optic buffering lines.',
    keyPoints: [
      'Multi-axis capstan & dancer tension closed-loop sync',
      'High-speed dual take-up tension regulation (< 0.05% error)',
      'Continuous vulcanization (CCV & PLCV) thermal profiling',
    ],
    platform: 'Siemens S7-1500 Advanced Platform',
    fieldbus: 'PROFINET IRT / PROFIBUS DP',
    safetyStd: 'IEC 61131-3 / Industrial Standards',
    deliverables: [
      'Multi-axis capstan speed & dancer tension closed-loop sync',
      'High-speed dual take-up & rewinding tension regulation',
      'Continuous vulcanization (CCV & PLCV) thermal profiling',
      'Fiber optic loose tube, secondary coating & buffering lines',
    ],
    machinery: [
      'Wire Drawing Machines',
      'CCV Continuous Vulcanization Lines',
      'Extrusion Insulating & Sheathing Lines',
      'Dual Take-Up / Dual Pay-Off Units',
      'Stranding & Armoring Machines',
      'Fiber Optic Secondary Coating Lines',
      'JFTC Telephone Cable Lines',
      'High-Speed Rewinding Units',
    ],
    kpiStats: [
      { label: 'Lines Commissioned', value: '150+' },
      { label: 'Energy Savings', value: 'Up to 28%' },
      { label: 'Tension Error', value: '< 0.05%' },
      { label: 'Global Handover', value: '14+ Countries' },
    ],
    siteIntro: 'With the advent of automation implementation in Wire and Cable Industry, productivity & Quality of the cable industry has been scaled up to a large extent. With our core competency in implementing Automation projects in Power Cable, Fibre Optic Cable, Control & Instrumentation & JFT.C cable industry.',
    domainExpertise: [
      {
        heading: 'Power Cable Industry',
        items: ['Wire Drawing Machine', 'Fine Wire Drawing Machine', 'Intermediate Wire Drawing Machine', 'Stranding Machine', 'Extrusion — Insulating / Sheathing / Jacketing Line', 'Drum Twister', 'Armouring Machine', 'Copper Taping / Steel Taping', 'Rewinding Line'],
      },
      {
        heading: 'Fibre Optics Cable Industry',
        items: ['Secondary Coating Line', 'SZ Stranding Machine', 'Ribbon Stranders', 'Aramid Yarn Server Machine', 'Fiber Payoff & Take-Up'],
      },
      {
        heading: 'Jelly Filled Cable Industry',
        items: ['Tandem Line / Insulating Line', 'Group Twinner', 'Drum Twister'],
      },
      {
        heading: 'Instrumentation Cable',
        items: ['Single Twist Machine', 'Double Twist Machine', 'Data Cable Sheathing Line', 'Mica Taping Line', 'Coiler Machine', 'Braiding Machine'],
      },
    ],
    caseStudies: [
      {
        title: 'CCV Line Automation',
        requirements: ['Migration from DC Motors to Energy efficient AC Motors', 'Automation of the complete line', 'Controlling the line through HMI — Operator Station', 'SCADA implementation'],
        implementation: ['Migration from DC Motors to Energy efficient AC Motors', 'Automation of the complete line', 'Controlling the line through HMI — Operator Station', 'SCADA implementation'],
        benefits: ['Industry 4.0 compliant equipment — live VFDs, PLC, HMI & SCADA', 'Reduced downtime of the machine', 'Energy saving', 'Extended equipment service life', 'Reduced Maintenance cost', 'Upgradation completed in record time as a retrofitting project'],
      },
      {
        title: 'PLCV Line Automation',
        requirements: ['Migration from DC Motors to Energy efficient AC Motors', 'Automation of the complete line', 'Controlling the line through HMI — Operator Station', 'SCADA implementation'],
        implementation: ['DC Motors replaced by AC Motors', 'Suitable AC Drives installed with PROFINET communication', 'S7-1500 PLC with various ET200 SP remote I/O stations installed', 'All existing Control panels replaced by new control panels', 'All machines from Pay-off to Take-up equipped with local PLC I/O station', 'PROFINET protocol', 'TP 1900 Comfort configured as operator station', 'WinCC 4.0 configured as SCADA Station'],
        benefits: ['Industry 4.0 compliant equipment — live VFDs, PLC, HMI & SCADA', 'Reduced downtime of the machine', 'Energy saving', 'Extended equipment service life', 'Reduced Maintenance cost', 'Upgradation completed in record time as a retrofitting project'],
      },
    ],
  },
  {
    id: 'rubber',
    num: '02',
    title: 'Rubber Processing Machines',
    subtitle: 'Tyres, Hoses & Technical Elastomers',
    badge: 'Heavy Industrial',
    iconType: 'rubber',
    tag: 'heavy',
    summary: 'Heavy-duty automation architectures for internal batch mixers, two-roll mixing mills, calenders, and hydraulic vulcanizing presses with millisecond thermal and torque coordination.',
    shortDescription: 'Heavy-duty automation for internal batch mixers, two-roll mixing mills, calenders, and hydraulic vulcanizing presses with millisecond coordination.',
    keyPoints: [
      'Precision recipe batch weighing & carbon black dosing',
      'Calender multi-motor roll speed ratio & nip regulation',
      'SIL-2 safety trip-bar & reverse nip emergency braking',
    ],
    platform: 'Siemens S7-1500 / S7-1200',
    fieldbus: 'Profinet / Modbus TCP',
    safetyStd: 'SIL-2 Safety Reverse Nip Interlocks',
    deliverables: [
      'Precision batch weighing, carbon black dosing & mixer ram control',
      'Calender line multi-motor roll speed ratio & nip gap regulation',
      'Hydraulic vulcanizing press multi-stage curing cycle timers',
      'Operator safety trip-bar & reverse nip emergency braking interlocks',
    ],
    machinery: [
      'Banbury Internal Mixers',
      'Two-Roll Mixing Mills (14"–26")',
      'Rubber Extruders & Strainers',
      '3 & 4-Roll Calender Lines',
      'Hydraulic Vulcanizing Presses',
      'Bale & Gasket Cutters',
    ],
    kpiStats: [
      { label: 'Curing Precision', value: '±0.5°C' },
      { label: 'Recipe Repeatability', value: '100%' },
      { label: 'Emergency Brake', value: '< 50ms' },
      { label: 'Polymer Yield', value: '99.4%' },
    ],
    siteIntro: 'With opportunity to execute automation and drive solutions in this segment, Rubber manufacturers are able to fulfil demand for new and retrofit solutions, in the process sustaining their profitable business.',
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['Rubber Compound Mixing Machine', 'Rubber Kneading Machine', 'Rubber Extrusion Machine — Both Hot & Cold Extrusion', 'Rubber Calander Line', 'Rubber Hydraulic Press', 'Bell Cutter Machine', 'Gasket Cutting Machine'],
      },
    ],
  },
  {
    id: 'pharma',
    num: '03',
    title: 'Pharmaceutical Industry',
    subtitle: 'FDA 21 CFR Part 11 & GAMP-5 Compliance',
    badge: 'Regulated / Cleanroom',
    iconType: 'pharma',
    tag: 'regulated',
    summary: 'Regulatory-compliant automation engineered for solid dosage, sterile injectables, and API synthesis, incorporating tamper-proof electronic signatures, electronic batch records (EBR), and complete audit trails.',
    shortDescription: 'Regulatory-compliant automation for solid dosage, sterile injectables, and API synthesis with tamper-proof batch records and audit trails.',
    keyPoints: [
      'FDA 21 CFR Part 11 & GAMP-5 validation protocol',
      'Tamper-proof electronic batch records (EBR) & signatures',
      'Fluid bed dryer (FBD) multi-phase thermal profiling',
    ],
    platform: 'Siemens SIMATIC S7-1500 + WinCC Pro',
    fieldbus: 'Industrial Ethernet / Profinet',
    safetyStd: 'FDA 21 CFR Part 11 / GAMP-5',
    deliverables: [
      'Tamper-proof electronic batch records (EBR) & electronic signatures',
      'Fluid bed dryer (FBD) multi-phase air velocity & temperature profiling',
      'Rapid mixer granulator (RMG) endpoint torque & binder dosing control',
      'Cleanroom differential pressure, HVAC & laminar air flow (LAF) telemetry',
    ],
    machinery: [
      'Fluid Bed Dryers (FBD / FBE)',
      'Rapid Mixer Granulators (RMG)',
      'Auto Tablet Coating Suites',
      'Sterile Vial & Ampoule Filling',
      'Cleanroom LAF & Isolator Skids',
      'Automatic Capsule Fillers',
    ],
    kpiStats: [
      { label: 'Compliance Level', value: '21 CFR Part 11' },
      { label: 'Data Integrity', value: '100% Audit Trail' },
      { label: 'Validation Protocol', value: 'GAMP-5 Standards' },
      { label: 'Batch Repeatability', value: '99.9%' },
    ],
    siteIntro: "The need for process optimisation, strict regulatory compliance (e.g. 21 CFR), across the Life Science Industry — product time-to-market must be extremely short, production highly efficient, and product quality impeccable. With our engineering team's efforts, we have implemented automation solutions in the pharmaceutical industry adhering to all regulatory norms.",
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['Isolator — Both Positive & Negative', 'Vial & Ampoule Filling Line', 'Granulation Machines — FBD, RMG & Auto Coater', 'Capsule Filling Machine', 'LAF Section', '21 CFR Implementation on both HMI & SCADA'],
      },
    ],
  },
  {
    id: 'chemical',
    num: '04',
    title: 'Chemical Industry',
    subtitle: 'Ex-Proof, Hazardous Zones & Continuous Processing',
    badge: 'ATEX Certified',
    iconType: 'chemical',
    tag: 'regulated',
    summary: 'Engineered for hazardous atmospheres with intrinsically safe I/O, cascade reactor temperature control, solvent recovery profiling, and certified IP65 flameproof enclosures.',
    shortDescription: 'Engineered for hazardous atmospheres with intrinsically safe I/O, cascade reactor temperature control, and certified IP65 flameproof enclosures.',
    keyPoints: [
      'ATEX / PESO certified IP65 flameproof enclosures',
      'Exothermic batch reactor automated cascade cooling & heating',
      'Continuous MBT recovery & solvent distillation sequence',
    ],
    platform: 'Siemens S7-1500F (Fail-Safe)',
    fieldbus: 'Profisafe / Intrinsic Safety Barriers',
    safetyStd: 'ATEX / PESO / IP65 Flameproof',
    deliverables: [
      'Exothermic batch reactor automated cascade cooling/heating profiling',
      'Solvent distillation reflux ratio & vacuum pressure automation',
      'Continuous MBT recovery & resin exchange column sequence control',
      'Hazardous zone gas detection & automated emergency purge interlocking',
    ],
    machinery: [
      'Exothermic Chemical Reactors',
      'Distillation & Rectification Columns',
      'MBT Solvent Recovery Units',
      'Chemical Metering & Dosing Skids',
      'Ultrasonic Cleaning Tanks',
      'Bulk Storage Tank Telemetry',
    ],
    kpiStats: [
      { label: 'Functional Safety', value: 'SIL-2 / SIL-3' },
      { label: 'Hazardous Rating', value: 'Zone 1 / 2 Ex' },
      { label: 'Flameproof Panel', value: 'IP65 Certified' },
      { label: 'Safety Interlocks', value: '100% Fail-Safe' },
    ],
    siteIntro: 'Our engineering team executes projects with S7-1500 PLC, Comfort Panel HMI & SCADA systems.',
    domainExpertise: [
      {
        heading: 'Domain Expertise Deployed In',
        items: ['MBT Recovery', 'Resin Column'],
      },
    ],
  },
  {
    id: 'oil-gas',
    num: '05',
    title: 'Oil & Gas Engineering',
    subtitle: 'SIL-3 Emergency Shutdown & Burner Safety',
    badge: 'Mission-Critical',
    iconType: 'oil-gas',
    tag: 'energy',
    summary: 'Mission-critical automation solutions with 1oo2D / 2oo3 voting architectures for emergency shutdown (ESD), burner management systems (BMS), and high-availability pipeline telemetry.',
    shortDescription: 'Mission-critical automation solutions with redundant voting architectures for emergency shutdown (ESD) and burner management systems (BMS).',
    keyPoints: [
      'IEC 61508 SIL-3 Emergency Shutdown (ESD) with hot failover',
      'Burner Management Systems (BMS) with fast flame trip',
      'Zero-bump CPU failover with redundant S7-1500R/H',
    ],
    platform: 'Siemens S7-1500R/H Redundant',
    fieldbus: 'Profisafe / Modbus TCP / OPC UA',
    safetyStd: 'IEC 61508 SIL-3 / NFPA 85 / 86',
    deliverables: [
      'SIL-3 rated Emergency Shutdown (ESD) with hot-standby CPU failover',
      'Burner Management Systems (BMS) with flame scanner sequence safety',
      'LPG / Propane gas storage, vaporizing skids & high-pressure profiling',
      'Rim Seal fire protection & emergency isolation valve (ESDV) logic',
    ],
    machinery: [
      'Gas Conditioning & Metering Skids',
      'Burner Management (BMS) Skids',
      'LPG / Propane Storage Plants',
      'Oxygen Generation Units',
      'Pipeline Pressure Regulating Skids',
      'Emergency Isolation Stations',
    ],
    kpiStats: [
      { label: 'Safety Integrity', value: 'SIL-3 Certified' },
      { label: 'CPU Failover', value: 'Zero Bump Transfer' },
      { label: 'Flame Fault Trip', value: '< 100ms' },
      { label: 'Plant Availability', value: '99.99%' },
    ],
    siteIntro: 'In a dynamic global industry like oil and gas, you need to manage costs, extract the most value possible from current assets and maximize up-time. Technology available today makes it possible to develop a truly connected enterprise and move closer to operational excellence. Modernizing internal processes with automation, along with better access to information about operations and maintenance, will help the industry streamline production and distribution and allow for a higher yield.',
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['BMS — Burner Management System', 'Propane Plant', 'LPG Plant', 'Oxygen Plant', 'Rim Seal'],
      },
    ],
  },
  {
    id: 'steel',
    num: '06',
    title: 'Iron & Steel Plants',
    subtitle: 'Rolling Mills, Tube Mills & Coil Slitting',
    badge: 'Heavy Metallurgy',
    iconType: 'steel',
    tag: 'heavy',
    summary: 'Ruggedized IP55 automation systems built for intense vibration, ambient heat, and heavy dynamic torque in hot/cold rolling mills, high-speed tube welding, and coil handling lines.',
    shortDescription: 'Ruggedized IP55 automation systems engineered for intense vibration, ambient heat, and heavy dynamic torque in rolling and tube mills.',
    keyPoints: [
      'Multi-stand rolling mill electronic line shaft sync',
      'Continuous high-speed tube mill flying shear control',
      'Common DC bus regenerative energy recovery (up to 24%)',
    ],
    platform: 'Siemens S7-1500 / Common DC Bus',
    fieldbus: 'Profinet IRT Isochronous Mode',
    safetyStd: 'Heavy Industrial IP55 / EN 60204-1',
    deliverables: [
      'Multi-stand rolling mill electronic line shaft & speed ratio sync',
      'High-speed tube mill continuous flying shear & welding sync',
      'Coil slitting & rewinder constant-tension closed-loop regulation',
      'Heavy EOT coil handling crane automated positioning & safety anti-sway',
    ],
    machinery: [
      'Cold & Hot Rolling Mills',
      'High-Speed Tube Mills',
      'Coil Slitting Lines',
      'Flying Cut-Off Friction Saws',
      'Heavy Coil Winders & Pay-Offs',
      'Heavy EOT Slab Cranes',
    ],
    kpiStats: [
      { label: 'Drive Sync', value: 'Microsecond Axis' },
      { label: 'Power Recovery', value: 'Up to 24% Regenerative' },
      { label: 'Mill Tech Handover', value: 'South Korea API' },
      { label: 'Panel Enclosure', value: 'IP55 Heavy Duty' },
    ],
    siteIntro: 'Carried out drives and automation projects in various applications across Iron & Steel plants.',
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['H&T Line', 'Tube Mill', 'Slitting Line', 'Winder', 'API Plant', 'Material Handling System (Crane)'],
      },
    ],
    caseStudies: [
      {
        title: 'API Plant Commissioning',
        requirements: ['Commissioning of API plant supplied by Mill Tech, South Korea'],
        implementation: ['Networking of G120, S110 & S120 VFD on PROFIBUS', 'Commissioning of individual VFD', 'PLC software development of the complete plant', 'HMI screens development of the machines', 'Servo drive commissioning', 'Hydro pressure testing', 'Offline seam tracking of UT'],
        benefits: ['Plant commissioned in record time without OEM support'],
      },
    ],
  },
  {
    id: 'furnace',
    num: '07',
    title: 'Furnace Automation',
    subtitle: 'Rotary, Hearth & Annealing Profiling',
    badge: 'Thermal Process',
    iconType: 'furnace',
    tag: 'energy',
    summary: 'Advanced thermal engineering for rotary, bogie hearth, and continuous annealing furnaces with multi-loop PID temperature profiling, fuel-air stoichiometric combustion, and safety sequencing.',
    shortDescription: 'Advanced thermal engineering for rotary, bogie hearth, and continuous annealing furnaces with multi-loop PID temperature profiling.',
    keyPoints: [
      'Multi-zone PID cascade ramp-soak (< ±1°C error)',
      'Automated oxygen-fuel stoichiometric ratio control',
      'AMS 2750 pyrometry & CQI-9 heat treat compliance',
    ],
    platform: 'Siemens S7-1200 / S7-1500 PID Loops',
    fieldbus: 'Profinet / Modbus RTU',
    safetyStd: 'AMS 2750 Pyrometry / CQI-9 Standards',
    deliverables: [
      'Multi-zone PID cascade ramp-soak temperature profiling (< ±1°C error)',
      'Automated oxygen-fuel stoichiometric ratio control for fuel reduction',
      'Protective atmosphere (Nitrogen/Hydrogen) furnace pressure regulation',
      'Fail-safe combustion purge, flame monitoring & emergency gas shut-off',
    ],
    machinery: [
      'Gas-Fired Rotary Furnaces',
      'Bogie Hearth Annealing Furnaces',
      'Controlled Atmosphere Chambers',
      'Electric Glass Toughening Lines',
      'Aluminum Melting Furnaces',
      'Continuous Tunnel Kilns',
    ],
    kpiStats: [
      { label: 'Thermal Uniformity', value: '±1°C Across Zones' },
      { label: 'Fuel Reduction', value: 'Up to 22%' },
      { label: 'Pyrometry Standard', value: 'AMS 2750 / CQI-9' },
      { label: 'Ramp-Soak Loops', value: '100% Automated' },
    ],
    siteIntro: 'Understanding the technical nature of heat treatment processes brings wide ranging benefits to a solution. We provide Engineering services for Electrical Design, Supply of Control Panels and automation of all types of Furnaces using PLC and SCADA.',
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['Gas fired Rotary Furnace (with Liquid Oxygen)', 'Gas fired Rotary Furnace (with Natural Gas)', 'Hearth Furnace', 'Glass Toughening Furnace (Electric)', 'Annealing Furnace'],
      },
    ],
  },
  {
    id: 'water',
    num: '08',
    title: 'Water Treatment Plants',
    subtitle: 'Automobile DM, Pharma RO & Industrial Effluent',
    badge: 'Utility & Purity',
    iconType: 'water',
    tag: 'utilities',
    summary: 'Turnkey process automation for reverse osmosis (RO), demineralization (DM), and wastewater plants with real-time analytical telemetry and VFD-modulated high-pressure booster pumps.',
    shortDescription: 'Turnkey process automation for reverse osmosis (RO), demineralization (DM), and wastewater plants with real-time analytical telemetry.',
    keyPoints: [
      'Automated multi-bed cation/anion regeneration',
      'Pharma purified water RO-EDI automated SIP/CIP',
      'Real-time pH neutralization with deadband PID logic',
    ],
    platform: 'Siemens S7-1200 / S7-1500 Architecture',
    fieldbus: 'Profinet / Modbus TCP Analyzers',
    safetyStd: 'FDA WFI Water Standards / CPCB ETP Norms',
    deliverables: [
      'Pharma purified water RO-EDI system with automated SIP/CIP sequencing',
      'Automobile DM water plant multi-bed cation/anion regeneration sequences',
      'Differential pressure automated backwash control for sand/carbon filters',
      'Real-time pH neutralization dosing with deadband-compensated PID logic',
    ],
    machinery: [
      'Reverse Osmosis (RO) Skids',
      'Demineralization (DM) Cation/Anion',
      'Multi-Grade Sand & Carbon Filters',
      'Ultra-Filtration (UF) Systems',
      'Industrial ETP Effluent Plants',
      'VFD Booster Pumping Stations',
    ],
    kpiStats: [
      { label: 'Water Purity', value: '< 0.1 µS/cm DM' },
      { label: 'Analytical Monitoring', value: 'Continuous TDS/pH' },
      { label: 'Membrane Flush', value: '100% Auto Cycle' },
      { label: 'Chemical Wastage', value: 'Zero Excess Dosing' },
    ],
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['Water Purifier in Automobile', 'Water Purifier in Pharma Industry — by controlling TDS & pH value'],
      },
    ],
  },
  {
    id: 'material-handling',
    num: '09',
    title: 'Material Handling Systems',
    subtitle: 'EOT Cranes, ASRS & High-Speed Conveyors',
    badge: 'Motion & Logistics',
    iconType: 'material-handling',
    tag: 'heavy',
    summary: 'Intelligent logistics and factory floor conveyance automation incorporating closed-loop flux vector drive coordination, electronic anti-sway algorithms, and barcode/RFID sorting logic.',
    shortDescription: 'Intelligent logistics and factory conveyance incorporating closed-loop flux vector drive coordination, electronic anti-sway, and ASRS logic.',
    keyPoints: [
      'EOT crane Long/Cross Travel & Hoist multi-axis sync',
      'Electronic anti-sway algorithm for high-speed loads',
      'ASRS stacker cranes & intelligent sorting conveyors',
    ],
    platform: 'SINAMICS S120 / G120 Vector Control',
    fieldbus: 'Profinet Wireless / Industrial Ethernet',
    safetyStd: 'SIL-2 Anti-Collision / ISO 13849',
    deliverables: [
      'EOT crane Long Travel, Cross Travel & Hoist multi-axis drive sync',
      'Electronic anti-sway sway-damping algorithm for high-speed crane loads',
      'High-speed packaging conveyor speed-matching & parcel sorting gates',
      'Automated Storage & Retrieval Systems (ASRS) encoder stacker logic',
    ],
    machinery: [
      'EOT Overhead Travelling Cranes',
      'High-Speed Sorting Conveyors',
      'ASRS Stacker Cranes',
      'Automated Transfer Trolleys',
      'Palletizing & Packaging Stations',
      'Heavy Coil Lifting Cranes',
    ],
    kpiStats: [
      { label: 'Motion Control', value: 'Closed-Loop Vector' },
      { label: 'Load Stability', value: 'Active Anti-Sway' },
      { label: 'Axis Coordination', value: 'Millisecond Sync' },
      { label: 'Duty Cycle', value: '24/7 Heavy Industrial' },
    ],
    domainExpertise: [
      {
        heading: 'Areas of Domain Expertise',
        items: ['Hoist, Long Travel & Cross Travel in Crane Application', 'Conveyor Application in FMCG'],
      },
    ],
  },
];
